import { useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/motion";

const PRESET_COLORS = ["#F97316", "#8B5CF6", "#5865F2", "#06B6D4", "#EC4899", "#10B981", "#EF4444", "#F59E0B"];
const GRADIENT_PRESETS = [["#F97316", "#EF4444"], ["#8B5CF6", "#EC4899"], ["#5865F2", "#06B6D4"], ["#06B6D4", "#10B981"], ["#EC4899", "#8B5CF6"], ["#F59E0B", "#EF4444"], ["#5865F2", "#8B5CF6"], ["#F97316", "#F59E0B"]];
const ICONS = ["Sparkles", "Crown", "Star", "Zap", "Heart", "Music2", "Gem", "Flame"];

function ColorPicker({ label, value, onChange, testId }) {
  return (
    <div data-testid={testId}>
      <label className="text-xs uppercase tracking-[0.15em] text-white/50 block mb-2">{label}</label>
      <div className="flex items-center gap-3">
        <label className="relative w-10 h-10 rounded-lg border border-white/15 overflow-hidden cursor-pointer shrink-0" style={{ background: value }}>
          <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" data-testid={`${testId}-wheel`} />
        </label>
        <input
          value={value}
          onChange={(e) => { const v = e.target.value; if (/^#[0-9a-fA-F]{0,6}$/.test(v)) onChange(v); }}
          maxLength={7}
          data-testid={`${testId}-hex`}
          className="w-28 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm font-mono text-white focus:outline-none focus:ring-2 focus:ring-blurple"
        />
      </div>
    </div>
  );
}

export default function Boosters() {
  const { site } = useSite();
  const [form, setForm] = useState({
    discord_username: "", role_name: "",
    style: "gradient", color1: "#5865F2", color2: "#06B6D4",
    icon: "Sparkles", icon_image: null,
  });
  const [iconMode, setIconMode] = useState("preset");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const Icon = Icons[form.icon] || Icons.Sparkles;
  const chipBg = form.style === "gradient"
    ? `linear-gradient(135deg, ${form.color1}, ${form.color2})`
    : form.color1;

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("PNG, JPG or WebP only");
      return;
    }
    if (file.size > 190_000) {
      toast.error("Image too large — keep it under 190KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, icon_image: reader.result });
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, icon_image: iconMode === "upload" ? form.icon_image : null };
      const res = await api.post("/public/boost-roles", payload);
      toast.success(res.data.message);
      setForm({ ...form, role_name: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="pt-16 min-h-screen" data-testid="boosters-page">
      <section className="hero-glow border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-volt font-semibold mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-volt inline-block" /> Booster perks
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight" style={{ fontWeight: 900 }} data-testid="boosters-headline">
              BOOST TWICE.<br /><span className="text-outline-blurple">WEAR YOUR OWN ROLE.</span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg mt-6 max-w-xl">
              Boost our support server twice — or invite 5 friends — and design your own custom role: name, solid or gradient colour, icon or your own uploaded image. Cosmetic only, zero permissions, created automatically by the bot.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8" data-testid="boost-form-card">
            <h2 className="font-heading text-2xl font-bold text-white mb-7">Design your role</h2>
            <form onSubmit={submit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-white/50 block mb-1.5">Discord username</label>
                  <input value={form.discord_username} onChange={(e) => setForm({ ...form, discord_username: e.target.value })} required minLength={2}
                    placeholder="e.g. NovaPulse" data-testid="boost-username"
                    className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-white/50 block mb-1.5">Role name</label>
                  <input value={form.role_name} onChange={(e) => setForm({ ...form, role_name: e.target.value })} required minLength={2} maxLength={40}
                    placeholder="Bass Monarch" data-testid="boost-role-name"
                    className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple" />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50 block mb-2">Style</label>
                <div className="grid grid-cols-2 gap-2 max-w-xs" data-testid="boost-style-picker">
                  {["solid", "gradient"].map((s) => (
                    <button type="button" key={s} onClick={() => setForm({ ...form, style: s })} data-testid={`boost-style-${s}`}
                      className={`py-2.5 rounded-lg text-sm font-semibold capitalize transition-colors duration-150 ${
                        form.style === s ? "bg-white text-void" : "border border-white/15 text-white/60 hover:text-white"
                      }`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50 block mb-2.5">
                  {form.style === "gradient" ? "Gradient presets" : "Colour presets"}
                </label>
                <div className="flex flex-wrap gap-2.5 mb-4" data-testid="boost-color-presets">
                  {form.style === "gradient"
                    ? GRADIENT_PRESETS.map(([a, b]) => (
                        <button type="button" key={a + b} onClick={() => setForm({ ...form, color1: a, color2: b })} aria-label={`${a} to ${b}`}
                          data-testid={`boost-gradient-${a.replace("#", "")}-${b.replace("#", "")}`}
                          className={`w-9 h-9 rounded-full transition-transform duration-150 ${form.color1 === a && form.color2 === b ? "scale-110 ring-2 ring-white ring-offset-2 ring-offset-void" : "hover:scale-105"}`}
                          style={{ background: `linear-gradient(135deg, ${a}, ${b})` }} />
                      ))
                    : PRESET_COLORS.map((c) => (
                        <button type="button" key={c} onClick={() => setForm({ ...form, color1: c })} aria-label={c}
                          data-testid={`boost-color-${c.replace("#", "")}`}
                          className={`w-9 h-9 rounded-full transition-transform duration-150 ${form.color1 === c ? "scale-110 ring-2 ring-white ring-offset-2 ring-offset-void" : "hover:scale-105"}`}
                          style={{ background: c }} />
                      ))}
                </div>
                <div className="flex gap-6 flex-wrap">
                  <ColorPicker label="Colour 1" value={form.color1} onChange={(v) => setForm({ ...form, color1: v })} testId="boost-color1" />
                  {form.style === "gradient" && (
                    <ColorPicker label="Colour 2" value={form.color2} onChange={(v) => setForm({ ...form, color2: v })} testId="boost-color2" />
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50 block mb-2">Icon</label>
                <div className="grid grid-cols-2 gap-2 max-w-xs mb-3" data-testid="boost-icon-mode">
                  {["preset", "upload"].map((m) => (
                    <button type="button" key={m} onClick={() => setIconMode(m)} data-testid={`boost-iconmode-${m}`}
                      className={`py-2.5 rounded-lg text-sm font-semibold capitalize transition-colors duration-150 ${
                        iconMode === m ? "bg-white text-void" : "border border-white/15 text-white/60 hover:text-white"
                      }`}>
                      {m === "preset" ? "Pick an icon" : "Upload image"}
                    </button>
                  ))}
                </div>
                {iconMode === "preset" ? (
                  <div className="flex flex-wrap gap-2.5" data-testid="boost-icon-picker">
                    {ICONS.map((name) => {
                      const I = Icons[name];
                      return (
                        <button type="button" key={name} onClick={() => setForm({ ...form, icon: name })} aria-label={name}
                          data-testid={`boost-icon-${name.toLowerCase()}`}
                          className={`w-11 h-11 rounded-lg border flex items-center justify-center transition-colors duration-150 ${
                            form.icon === name ? "border-blurple bg-blurple/15 text-white" : "border-white/10 text-white/40 hover:text-white hover:border-white/30"
                          }`}>
                          <I size={18} />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div data-testid="boost-icon-upload">
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onUpload} className="hidden" data-testid="boost-icon-file" />
                    <button type="button" onClick={() => fileRef.current?.click()} data-testid="boost-icon-upload-button"
                      className="inline-flex items-center gap-2 border border-dashed border-white/25 hover:border-volt text-white/60 hover:text-white text-sm font-semibold px-5 py-3 rounded-lg transition-colors duration-150">
                      <Icons.ImagePlus size={16} /> {form.icon_image ? "Change image" : "Choose image (PNG/JPG/WebP, <190KB)"}
                    </button>
                    {form.icon_image && (
                      <img src={form.icon_image} alt="Role icon preview" className="mt-3 w-12 h-12 rounded-lg object-cover border border-white/15" data-testid="boost-icon-image-preview" />
                    )}
                    <p className="text-xs text-white/35 mt-2.5 flex items-center gap-1.5">
                      <Icons.ShieldCheck size={13} className="text-emerald-400" />
                      Uploaded icons are reviewed by staff before the role is created — nothing inappropriate gets through.
                    </p>
                  </div>
                )}
              </div>

              <button type="submit" disabled={busy} data-testid="boost-submit"
                className="w-full bg-blurple hover:bg-[#4752c4] disabled:opacity-50 text-white font-heading font-bold py-3.5 rounded-full transition-colors duration-200">
                {busy ? "Sending…" : "Request my custom role"}
              </button>
              <p className="text-xs text-white/35 leading-relaxed">
                GrooveMusic verifies your two boosts automatically, then the bot creates the role in the support server by itself. No permissions are ever attached.
              </p>
            </form>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 font-semibold mb-4">Live preview</p>
            <div className="rounded-2xl bg-[#313338] border border-white/10 overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.5)]" data-testid="boost-preview">
              <div className="bg-[#1e1f22] px-4 py-3 flex items-center gap-2 border-b border-black/40">
                <Icons.Hash size={16} className="text-[#80848e]" />
                <span className="text-sm font-semibold text-white/90">the-groove</span>
              </div>
              <div className="p-5 space-y-5">
                <div className="flex gap-3.5">
                  <img src="/assets/logo.jpg" alt="GrooveMusic" className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="text-sm">
                      <span className="font-semibold text-white">GrooveMusic</span>
                      <span className="ml-2 text-[10px] font-bold bg-blurple text-white rounded px-1.5 py-0.5 align-middle">APP</span>
                      <span className="ml-2 text-xs text-[#949ba4]">Today at 9:41 PM</span>
                    </p>
                    <p className="text-sm text-[#dbdee1] mt-0.5">
                      {form.discord_username || "you"} just boosted twice — custom role unlocked.
                    </p>
                    <div className="mt-3 bg-[#2b2d31] border-l-4 rounded-md p-4" style={{ borderColor: form.color1 }}>
                      <div
                        className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold text-white"
                        style={{ background: chipBg }}
                        data-testid="boost-role-preview"
                      >
                        {iconMode === "upload" && form.icon_image
                          ? <img src={form.icon_image} alt="" className="w-4 h-4 rounded-full object-cover" />
                          : <Icon size={14} />}
                        {form.role_name || "Your role name"}
                      </div>
                      <p className="text-xs text-[#949ba4] mt-3 font-mono">
                        {form.style} · {form.color1}{form.style === "gradient" ? ` → ${form.color2}` : ""} · permissions: none
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 space-y-4">
              {[
                ["Boost twice or invite 5 friends", "The bot watches boosts and invites automatically."],
                ["Pick your name, colours and icon", "Solid or gradient, preset icon or your own image."],
                ["The bot creates it by itself", "Preset icons apply automatically. Uploaded icons get a quick staff review first."],
              ].map(([t, d], i) => (
                <div key={t} className="flex gap-4 items-start" data-testid={`boost-step-${i}`}>
                  <span className="font-display text-outline text-2xl shrink-0" style={{ fontWeight: 700 }}>{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <p className="font-heading font-bold text-white text-sm">{t}</p>
                    <p className="text-sm text-muted-foreground">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
